import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { ScrollView, DeviceEventEmitter } from "~/ui/primitives";
import { observer } from "mobx-react-lite";
import Profile from "./components/profile";
import useNavigation from "~hooks/useNavigation";
import styled, { useTheme } from "~/ui/styled";
import {
  StartFlex,
  ColumnFlex,
  CenterFlex,
  BetweenFlex,
  IconFont,
  Text,
  Button,
} from "~components/El";
import { Tabs } from "~components/Tabs";
import { useStores } from "~models";
import { useWebview, buildWebviewBaseUrl } from "~components/WebviewContext";

interface ColumnItemProps {
  title: string;
  content?: string;
  suffix?: string;
  icon: string;
  iconColor: string;
  iconSize: number;
  bg: string;
  onClick: () => void;
}

const Container = styled(ColumnFlex)`
  /* flex: 1; */
  height: auto;
  flex-direction: column;
`;
const tabs = [
  { label: "设置", value: "setting", icon: "sz1" },
  { label: "账户绑定", value: "account", icon: "zg" },
];

const ColumnItem: React.FC<{ item: ColumnItemProps }> = ({ item }) => {
  const theme = useTheme();
  return (
    <CenterFlex width="100%" padding="12px 0" onClick={item.onClick}>
      <BetweenFlex width="100%" height="auto">
        <StartFlex>
          <CenterFlex
            width="36px"
            height="36px"
            borderRadius="18px"
            bg={item.bg}
          >
            <IconFont
              name={item.icon}
              size={item.iconSize}
              color={item.iconColor}
            />
          </CenterFlex>
          <ColumnFlex ml="10px">
            <Text
              fontSize={theme.fontSizes.md}
              color={theme.colors.text}
              fontWeight="600"
            >
              {item.title}
            </Text>
            <Text
              fontSize={theme.fontSizes.sm}
              color={theme.colors.secondary}
              mt="2px"
            >
              {item.content}
            </Text>
          </ColumnFlex>
        </StartFlex>
        <StartFlex minWidth={"max-content"} width="auto">
          {item.suffix ? (
            <Text
              fontSize={theme.fontSizes.sm}
              color={theme.colors.infoText}
              mr="8px"
            >
              {item.suffix}
            </Text>
          ) : null}
          <IconFont name="right" size={12} color={theme.colors.secondary} />
        </StartFlex>
      </BetweenFlex>
    </CenterFlex>
  );
};

const Me: React.FC = () => {
  const theme = useTheme();
  const navigation = useNavigation();
  const { apiStore, authStore } = useStores();
  const [tab, setTab] = useState("setting");
  const [bindAccountInfo, setBindAccountInfo] = useState(null);

  const { openWebview, visible: webviewVisible, preloadWebview } = useWebview();

  const getBindAccountInfo = useCallback(async () => {
    try {
      const res = await apiStore.getBindAccountInfo();
      if (res.data) {
        setBindAccountInfo(res.data);
      }
    } catch (error) {}
  }, [apiStore]);
  const onTabPress = useCallback((item) => {
    setTab(item.value);
  }, []);

  const onPressFn = useCallback(
    (type: string) => {
      if (type === "RECHARGE") {
        openWebview({ path: "/user/recharge" });
      } else if (type === "WITHDRAW") {
        openWebview({ path: "/user/withdraw" });
      } else {
        openWebview({ path: "/user/billRecordLogs" });
      }
    },
    [openWebview],
  );

  const onPressWebview = useCallback(
    (name: string) => {
      if (name === "bopay") {
        openWebview({
          path: "/user/bopay-manage",
          state: { name, max_account: 1 },
        });
      } else if (name === "kkpay") {
        openWebview({
          path: "/user/kkpay-manage",
          state: { name, max_account: 1 },
        });
      } else {
        openWebview({
          path: "/user/account-manage",
          state: { name, max_account: 3 },
        });
      }
    },
    [openWebview],
  );

  const settingColumns = useMemo<ColumnItemProps[]>(() => {
    return [
      {
        title: "安全",
        content: "登录密码、支付密码、手机号、邮箱验证、谷歌验证",
        suffix: "",
        icon: "scrz",
        iconColor: "#07c160",
        iconSize: 16,
        bg: "rgba(7, 193, 96, 0.1)",
        onClick: () => {
          navigation.push("Setting", {
            title: "安全设置",
            type: "security",
          });
        },
      },
      {
        title: "隐私",
        content: "添加方式、好友验证、群聊验证、频道验证、上线时...",
        suffix: "",
        icon: "scrz",
        iconColor: "#ec8027",
        iconSize: 16,
        bg: "rgba(236, 128, 39, 0.1)",
        onClick: () => {
          navigation.push("Setting", {
            title: "隐私设置",
            type: "privacy",
          });
        },
      },
      {
        title: "通用",
        content: "字体大小、语言、官网、登录设备、数据清理、退出...",
        suffix: "",
        icon: "scrz",
        iconColor: "#3679d6",
        iconSize: 16,
        bg: "rgba(54, 121, 214, 0.1)",
        onClick: () => {
          navigation.push("Setting", {
            title: "通用",
            type: "general",
          });
        },
      },
      // {
      //   title: "消息设置",
      //   content: "消息通知、通知声音、震动提醒、免打扰模式",
      //   suffix: "",
      //   icon: "scrz",
      //   iconColor: "#ff6565",
      //   iconSize: 16,
      //   bg: "rgba(255, 101, 101, 0.1)",
      //   onClick: () => {
      //     navigation.push("Setting", {
      //       title: "消息设置",
      //       type: "message",
      //     });
      //   },
      // },
      {
        title: "聊天设置",
        content: "群发助手、表情包、回车键发送、聊天消息定时自毁",
        suffix: "",
        icon: "scrz",
        iconColor: "#c922c4",
        iconSize: 16,
        bg: "rgba(201, 34, 196, 0.1)",
        onClick: () => {
          navigation.push("Setting", {
            title: "聊天设置",
            type: "chat",
          });
        },
      },
    ];
  }, []);

  const accountColumns = useMemo<ColumnItemProps[]>(() => {
    return [
      {
        title: "USDT",
        content: "绑定USDT-TRC20钱包地址",
        suffix:
          bindAccountInfo?.usdtpay?.length > 0
            ? `已绑定${bindAccountInfo.usdtpay.length}个地址`
            : "未绑定",
        icon: "scrz",
        iconColor: "#07c160",
        iconSize: 16,
        bg: "rgba(25, 152, 111, 0.1)",
        onClick: () => {
          onPressWebview("usdtpay");
        },
      },
      {
        title: "支付宝",
        content: "绑定支付宝账户",
        suffix:
          bindAccountInfo?.alipay?.length > 0
            ? `已绑定${bindAccountInfo.alipay.length}个账户`
            : "未绑定",
        icon: "scrz",
        iconColor: "#07c160",
        iconSize: 16,
        bg: "rgba(6, 180, 253, 0.1)",
        onClick: () => {
          onPressWebview("alipay");
        },
      },
      {
        title: "微信",
        content: "绑定微信账户",
        suffix:
          bindAccountInfo?.wxpay?.length > 0
            ? `已绑定${bindAccountInfo.wxpay.length}个账户`
            : "未绑定",
        icon: "scrz",
        iconColor: "#07c160",
        iconSize: 16,
        bg: "rgba(107, 204, 3, 0.1)",
        onClick: () => {
          onPressWebview("wxpay");
        },
      },
      {
        title: "K-Plus",
        content: "绑定K-Plus钱包",
        suffix: bindAccountInfo?.kkpay?.length > 0 ? `已绑定` : "未绑定",
        icon: "scrz",
        iconColor: "#07c160",
        iconSize: 16,
        bg: "rgba(101, 122, 255, 0.1)",
        onClick: () => {
          onPressWebview("kkpay");
        },
      },
      // {
      //   title: "波币钱包",
      //   content: "绑定BOPAY钱包",
      //   suffix: bindAccountInfo?.bopay?.length > 0 ? `已绑定` : "未绑定",
      //   icon: "scrz",
      //   iconColor: "#07c160",
      //   iconSize: 16,
      //   bg: "rgba(53, 106, 253, 0.1)",
      //   onClick: () => {
      //     onPressWebview('bopay')
      //   }
      // },
      {
        title: "银行卡",
        content: "绑定银行卡用于提现",
        suffix:
          bindAccountInfo?.bank?.length > 0
            ? `已绑定${bindAccountInfo.bank.length}个账户`
            : "未绑定",
        icon: "scrz",
        iconColor: "#fa5251",
        iconSize: 16,
        bg: "rgba(250, 82, 81, 0.1)",
        onClick: () => {
          onPressWebview("bank");
        },
      },
    ];
  }, [bindAccountInfo]);

  const columns = useMemo(() => {
    if (tab === "account") return accountColumns;
    return settingColumns;
  }, [tab, accountColumns, settingColumns]);

  const prevWebviewVisibleRef = useRef(false);

  useEffect(() => {
    void getBindAccountInfo();

    const sub = DeviceEventEmitter.addListener("onBindAccountInfo", () => {
      void getBindAccountInfo();
    });

    return () => sub.remove();
  }, [getBindAccountInfo]);

  useEffect(() => {
    if (authStore.hasToken) {
      preloadWebview(buildWebviewBaseUrl(authStore.token));
    }
  }, [authStore.hasToken, authStore.token, preloadWebview]);

  useEffect(() => {
    if (prevWebviewVisibleRef.current && !webviewVisible) {
      void getBindAccountInfo();
    }
    prevWebviewVisibleRef.current = webviewVisible;
  }, [webviewVisible, getBindAccountInfo]);

  const content = (
    <>
      <Profile onClick={onPressFn} />
      <ColumnFlex
        width="100%"
        height="auto"
        padding="12px"
        borderRadius="16px 16px 0 0"
        bg={theme.colors.blockBackground}
      >
        <Tabs
          tabs={tabs}
          active={tab}
          height={36}
          onChange={(item) => onTabPress(item)}
        />
        <ColumnFlex padding="10px 0">
          {columns.map((item, index) => (
            <ColumnItem item={item} key={index} />
          ))}
        </ColumnFlex>
      </ColumnFlex>
    </>
  );

  return (
    <ScrollView
      style={{ flex: 1, minHeight: 0, width: "100%" }}
      contentContainerStyle={{ flexGrow: 1, width: "100%" }}
    >
      {content}
    </ScrollView>
  );
};

export default observer(Me);
